import type { HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { ToastService } from '@capturum/ui/api';
import { WINDOW } from '@core/configs/token.config';
import { OtpCode } from '@core/enums/otp.enum';
import { OtpDialogConfig, OtpHttpErrorResponse } from '@core/interfaces/otp.interface';
import { interceptorHelpersProxy } from '@core/utils/utils-proxy';
import { TranslateService } from '@ngx-translate/core';
import { CurrentPasswordDialogComponent } from '@shared/components/current-password-dialog/current-password-dialog.component';
import { NeqtoAuthService } from '@shared/services/neqto-auth.service';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { catchError, EMPTY, filter, finalize, from, switchMap, take } from 'rxjs';

export const notAuthorizedInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const SHOULD_LOGOUT_ERRORS = ['jwt: token is blocked', 'access denied'];
  const unauthorizedErrors = ['unauthorized', 'invalid token', 'token expired'];
  const toastService = inject(ToastService);
  const translateService = inject(TranslateService);
  const dialogService = inject(DialogService);
  const authService = inject(NeqtoAuthService);
  const router = inject(Router);
  const window = inject(WINDOW);
  const otpStatusCodes = [OtpCode.failed, OtpCode.missing];
  const reAuthenticatedOTPHeader = 'X-Auth-OTP';
  const notAuthorizeHandle = (): void => {
    authService
      .checkJwtToken()
      .pipe(
        switchMap((response) => {
          // we will get boolean or RefreshTokenData
          // if boolean, response from logout, so we redirect to login
          if (typeof response === 'boolean') {
            return from(router.navigate([`auth/login`])).pipe(
              finalize(() => {
                window.location.reload();
              }),
            );
          }

          return EMPTY;
        }),
      )
      .subscribe();
  };

  return next(req).pipe(
    catchError((errorResponse: HttpErrorResponse) => {
      if (errorResponse instanceof HttpErrorResponse) {
        switch (errorResponse.status) {
          case 401: {
            const isUnauthorizedError = unauthorizedErrors.some((error) => {
              return errorResponse.error?.error?.toString()?.toLowerCase().includes(error);
            });

            if (isUnauthorizedError) {
              return from(router.navigate([`auth/login`])).pipe(
                switchMap(() => {
                  return EMPTY;
                }),
                finalize(() => {
                  window.location.reload();
                }),
              );
            }

            if (
              errorResponse.error &&
              typeof errorResponse.error === 'string' &&
              SHOULD_LOGOUT_ERRORS.some((error) => errorResponse.error.includes(error))
            ) {
              return authService.logout().pipe(
                switchMap(() => {
                  return EMPTY;
                }),
                finalize(() => {
                  window.location.reload();
                }),
              );
            }

            const otpHttpErrorResponse = errorResponse.error as OtpHttpErrorResponse;

            if (otpStatusCodes.includes(otpHttpErrorResponse?.code)) {
              const purpose = otpHttpErrorResponse.purpose;

              // open dialog to enter current password
              return dialogService
                .open(CurrentPasswordDialogComponent, {
                  closable: true,
                  header: translateService.instant('otp.current-password.label'),
                  styleClass: 'dialog-xs',
                  data: {
                    purpose,
                  },
                } as DynamicDialogConfig<OtpDialogConfig>)
                .onClose.pipe(
                  filter(Boolean),
                  take(1),
                  switchMap((otp: string) => {
                    const request = req.clone({
                      setHeaders: {
                        [reAuthenticatedOTPHeader]: otp,
                      },
                    });

                    return next(request);
                  }),
                );
            }

            toastService.error(
              translateService.instant('toast.error.title'),
              translateService.instant('not-found.title'),
            );
            notAuthorizeHandle();

            return interceptorHelpersProxy.handleError(errorResponse);
          }

          case 423:
            return next(req);

          default:
            break;
        }
      }

      return interceptorHelpersProxy.handleError(errorResponse);
    }),
  );
};
